/**
 * =============================================================================
 * IDS CRM Web - Opportunity (Group) Detail — minimal Phase 4 surface
 * =============================================================================
 *
 * The grouped commercial record: header, line items with approval state, and
 * the APPROVAL PANEL (per-item approve/reject with mandatory rejection note +
 * Approve All) — the spec's "review from the Opportunity view". Also carries
 * the resolution paths: revise pricing / revert to default, cancel line,
 * cancel opportunity. Phase 5 expands this page (quotes, POs, assets, history).
 *
 * =============================================================================
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import BackToList, { useBackToList } from '../components/BackToList';
import { useAuth } from '../context/AuthContext';
import AuditLogPanel from '../components/AuditLogPanel';
import MarkLostModal from '../components/MarkLostModal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import VariationModal from '../components/VariationModal';
import { useToast } from '../components/Toast';
import { authedFileHref } from '../utils/files';
import { formatMoney } from '../utils/money';
import { formatDateTime } from '../utils/format';
import { acceptanceLabel } from '../utils/orders';
import { canAcceptOrders } from '../utils/permissions';
import './OpportunityGroupDetail.css';
import './CommercialLayout.css';

const toMajor = (minor) => (minor || minor === 0 ? (minor / 100).toFixed(2) : '');
const toMinor = (major) => Math.round((Number(major) || 0) * 100);

function lineStatus(item) {
  if (item.cancelled_at) return { label: 'Cancelled', className: 'chip-muted' };
  if (item.lost_at) return { label: 'Lost', className: 'chip-red' };
  if (item.won_at) return { label: 'Won', className: 'chip-green' };
  if (item.approval_status === 'pending') return { label: 'Awaiting Approval', className: 'chip-amber' };
  if (item.approval_status === 'rejected') return { label: 'Action Required', className: 'chip-red' };
  return null;
}

/** Modal: revise a line's pricing (schedule for product lines, plain price for
 *  non-product lines) or revert a product line to its default. */
const OUTCOME_META = {
  won: { label: 'Won', chip: 'chip-green' },
  partially_won: { label: 'Partially won', chip: 'chip-amber' },
  lost: { label: 'Lost', chip: 'chip-red' }
};

function RevisePricingModal({ item, authFetch, onClose, onSaved }) {
  const isNonProduct = item.line_type === 'non_product';
  const [schedule, setSchedule] = useState((item.pricing_schedule || []).map(entry => ({ ...entry })));
  const [price, setPrice] = useState(toMajor(item.value_minor_units));
  const [reason, setReason] = useState(item.special_price_reason || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async (revertToDefault = false) => {
    setSaving(true);
    setError('');
    try {
      const body = isNonProduct
        ? { price_minor_units: toMinor(price) }
        : (revertToDefault ? { schedule: [] } : { schedule, special_price_reason: reason });
      const response = await authFetch(`/api/v1/opportunities/${item.opportunity_id}/pricing`, {
        method: 'PUT',
        body: JSON.stringify(body)
      });
      if (!response.ok) {
        const responseBody = await response.json().catch(() => ({}));
        throw new Error(responseBody.message || 'Failed to update pricing');
      }
      onSaved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Revise Pricing — {item.product ? item.product.replace(/_/g, ' ') : item.description}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="form-error">{error}</div>}
          {isNonProduct ? (
            <div className="form-group">
              <label>Price (£) *</label>
              <input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} />
            </div>
          ) : (
          <table className="group-schedule-table">
            <thead>
              <tr><th>Label</th><th>Amount (£)</th><th>Type</th></tr>
            </thead>
            <tbody>
              {schedule.map((entry, index) => (
                <tr key={entry.key || index}>
                  <td>{entry.label}</td>
                  <td>
                    <input
                      type="number" min="0" step="0.01"
                      value={toMajor(entry.amount_minor_units)}
                      onChange={(e) => setSchedule(prev => prev.map((s, i) => (
                        i === index ? { ...s, amount_minor_units: toMinor(e.target.value) } : s
                      )))}
                    />
                  </td>
                  <td>{entry.recurring ? 'Monthly' : 'One-off'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
          {!isNonProduct && (
            <div className="form-group">
              <label>Internal reason (required if pricing deviates from default)</label>
              <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
          )}
        </div>
        <div className="modal-footer">
          {!isNonProduct && (
            <button className="btn-secondary" disabled={saving} onClick={() => save(true)} title="Reset to the configured default schedule — clears any approval requirement">
              Revert to Default
            </button>
          )}
          <button className="btn-primary" disabled={saving} onClick={() => save(false)}>
            {saving ? 'Saving…' : 'Save & Resubmit'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Modal: upload a purchase order (file + optional PO number + line links). */
function UploadPoModal({ group, eligibleIds, authFetch, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [selected, setSelected] = useState(() => {
    const initial = {};
    for (const item of group.line_items || []) {
      if (!item.lost_at && !item.cancelled_at) initial[item.opportunity_id] = eligibleIds.has(item.opportunity_id);
    }
    return initial;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleFile = (picked) => {
    if (!picked) return;
    const reader = new FileReader();
    reader.onload = () => { setFile(reader.result); setFileName(picked.name); };
    reader.readAsDataURL(picked);
  };

  const upload = async () => {
    if (!file) { setError('Pick a PDF or image of the purchase order'); return; }
    setBusy(true);
    setError('');
    try {
      const lineIds = Object.keys(selected).filter(id => selected[id]);
      const response = await authFetch(`/api/v1/opportunity-groups/${group.group_id}/purchase-orders`, {
        method: 'POST',
        body: JSON.stringify({
          file, name: fileName,
          po_number: poNumber.trim() || null,
          notes: notes.trim() || null,
          line_item_ids: lineIds
        })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to upload the purchase order');
      }
      onDone();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Upload Purchase Order</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="form-error">{error}</div>}
          <div className="form-group">
            <label>File (PDF or image) *</label>
            <input type="file" accept="application/pdf,image/*" onChange={(e) => handleFile(e.target.files?.[0])} />
          </div>
          <div className="form-row compact">
            <div className="form-group">
              <label>PO number (optional)</label>
              <input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="Not all customers use POs" />
            </div>
          </div>
          <div className="form-group">
            <label>Covers</label>
            {(group.line_items || []).filter(item => !item.lost_at && !item.cancelled_at).map(item => (
              <label key={item.opportunity_id} className="checkbox-label" style={{ display: 'block' }}>
                <input
                  type="checkbox"
                  checked={!!selected[item.opportunity_id]}
                  onChange={(e) => setSelected(prev => ({ ...prev, [item.opportunity_id]: e.target.checked }))}
                />
                <span> {item.opp_ref} — {item.line_type === 'product' ? (item.product || '').replace(/_/g, ' ') : item.description}</span>
              </label>
            ))}
            <small className="field-hint">Untick everything for a PO covering the whole opportunity.</small>
          </div>
          <div className="form-group">
            <label>Notes (optional)</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn-primary" onClick={upload} disabled={busy || !file}>
            {busy ? 'Uploading…' : 'Upload PO'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Modal: collect a mandatory reason (line/group cancel). */
function ReasonModal({ title, actionLabel, onConfirm, onClose }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="form-error">{error}</div>}
          <div className="form-group">
            <label>Reason *</label>
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Back</button>
          <button
            className="btn-danger"
            onClick={() => {
              if (!reason.trim()) { setError('A reason is required'); return; }
              onConfirm(reason.trim());
            }}
          >
            {actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Orders (spec 3.3, decision 6): a team member checks the order is complete
 * (farm details, a sufficient survey from the distributor) and accepts it.
 * The CRM records who and when; nothing is enforced. Taking an acceptance
 * back needs a note saying what is missing.
 */
function OrderAcceptance({ group, canAccept, authFetch, onChanged, showToast }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const accepted = Boolean(group.order_accepted_at);

  const send = async (action) => {
    if (action === 'unaccept' && !note.trim()) {
      setError('Say what is missing or wrong with the order.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await authFetch(`/api/v1/opportunity-groups/${group.group_id}/${action}`, {
        method: 'POST',
        body: JSON.stringify({ note: note.trim() || null })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'That could not be saved. Try again.');
      setNote('');
      showToast(action === 'accept' ? 'Order accepted.' : 'Order acceptance taken back.', 'success');
      await onChanged();
    } catch (err) {
      setError(err.message === 'Failed to fetch' ? 'That could not be sent. Check your signal and try again.' : err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`order-acceptance ${accepted ? 'accepted' : 'pending'}`}>
      <div className="order-acceptance-head">
        <strong>{acceptanceLabel(group, formatDateTime)}</strong>
        {accepted && group.order_accept_note && <span className="order-acceptance-note">“{group.order_accept_note}”</span>}
      </div>
      {!accepted && (
        <p className="order-acceptance-hint">
          Check the farm details (customer, site, address, contact) and that the distributor’s site survey is
          sufficient and attached, then accept the order.
        </p>
      )}
      {canAccept && group.status !== 'cancelled' && (
        <div className="order-acceptance-actions">
          <input
            value={note}
            maxLength={2000}
            placeholder={accepted ? 'What is missing or wrong (required)…' : 'Note (optional)…'}
            aria-label={accepted ? 'What is missing or wrong' : 'Acceptance note'}
            onChange={(e) => setNote(e.target.value)}
          />
          {accepted ? (
            <button className="btn-secondary btn-small" disabled={busy} onClick={() => send('unaccept')}>Take back acceptance</button>
          ) : (
            <button className="btn-primary btn-small" disabled={busy} onClick={() => send('accept')}>Accept order</button>
          )}
        </div>
      )}
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}

function OpportunityGroupDetail() {
  const { id } = useParams();
  const { authFetch, user } = useAuth();
  const navigate = useNavigate();
  // Back goes to the Pipeline page as it was left.
  const backToList = useBackToList('/pipeline');

  const [group, setGroup] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [notes, setNotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [busy, setBusy] = useState(false);
  const [reviseItem, setReviseItem] = useState(null);
  // Phase R1: won line items are pricing-locked — changes go via a contract variation
  const [variationItem, setVariationItem] = useState(null);
  const { toastElement, showToast } = useToast();
  const [cancelTarget, setCancelTarget] = useState(null); // {type:'line', item} | {type:'group'}
  const [lostItem, setLostItem] = useState(null);
  const [wonTarget, setWonTarget] = useState(null); // R5a (DL-12): non-product line about to be marked won
  const [quotes, setQuotes] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [stagesByType, setStagesByType] = useState({});
  const [showPoModal, setShowPoModal] = useState(false);
  const [historyKey, setHistoryKey] = useState(0); // bumped to refresh the history feed

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [groupRes, pendingRes] = await Promise.all([
        authFetch(`/api/v1/opportunity-groups/${id}`),
        authFetch('/api/v1/approvals/pending')
      ]);
      if (!groupRes.ok) throw new Error('Opportunity not found');
      const groupData = await groupRes.json();
      setGroup(groupData.data);
      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();
        setPendingRequests((pendingData.data || []).filter(r => r.group_id === id));
      }
      try {
        const [quotesRes, posRes, stagesRes] = await Promise.all([
          authFetch(`/api/v1/quotes?opportunity_group_id=${id}`),
          authFetch(`/api/v1/opportunity-groups/${id}/purchase-orders`),
          authFetch('/api/v1/opportunities/pipeline-stages')
        ]);
        if (quotesRes.ok) setQuotes((await quotesRes.json()).data || []);
        if (posRes.ok) setPurchaseOrders((await posRes.json()).data || []);
        if (stagesRes.ok) setStagesByType((await stagesRes.json()).data || {});
      } catch (quotesError) {
        // Supplementary sections
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, id]);

  useEffect(() => { load(); }, [load]);

  const decideRequest = async (request, decision) => {
    const note = (notes[request.request_id] || '').trim();
    if (decision === 'rejected' && !note) {
      setActionError('A rejection reason is required');
      return;
    }
    setBusy(true);
    setActionError('');
    try {
      const response = await authFetch(`/api/v1/approvals/${request.request_id}/decision`, {
        method: 'PUT',
        body: JSON.stringify({ decision, note: note || null })
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to record decision');
      }
      await load();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const approveAll = async () => {
    setBusy(true);
    setActionError('');
    try {
      const response = await authFetch(`/api/v1/opportunity-groups/${id}/approve-all`, {
        method: 'POST',
        body: JSON.stringify({})
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to approve items');
      }
      await load();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const cancelLine = async (item, reason) => {
    setBusy(true);
    setActionError('');
    try {
      const response = await authFetch(`/api/v1/opportunities/${item.opportunity_id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason })
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to cancel line item');
      }
      setCancelTarget(null);
      await load();
    } catch (err) {
      setActionError(err.message);
      setCancelTarget(null);
    } finally {
      setBusy(false);
    }
  };

  // R5a (DL-12): winning a non-product line is an explicit, price-locking mark.
  const markWon = async (item) => {
    setBusy(true);
    setActionError('');
    try {
      const response = await authFetch(`/api/v1/opportunities/${item.opportunity_id}/mark-won`, { method: 'POST' });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to mark the line item won');
      }
      setWonTarget(null);
      showToast('Line item marked won — price locked', 'success');
      setHistoryKey(k => k + 1);
      await load();
    } catch (err) {
      setActionError(err.message);
      setWonTarget(null);
    } finally {
      setBusy(false);
    }
  };

  const cancelGroup = async (reason) => {
    setBusy(true);
    setActionError('');
    try {
      const response = await authFetch(`/api/v1/opportunity-groups/${id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason })
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to cancel opportunity');
      }
      setCancelTarget(null);
      await load();
    } catch (err) {
      setActionError(err.message);
      setCancelTarget(null);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="page-loading">Loading opportunity…</div>;
  if (error) return <div className="page-error">{error} — <BackToList to="/pipeline">back to Commercial</BackToList></div>;
  if (!group) return null;

  const items = group.line_items || [];
  const currency = items[0]?.currency || 'GBP';
  const canManage = ['commercial', 'manager', 'admin'].includes(user?.role);
  const ownItem = (item) => !item.won_at && !item.lost_at && !item.cancelled_at;
  // Phase R1: won (and still live) lines are pricing-locked — variation only
  const lockedItem = (item) => item.won_at && !item.lost_at && !item.cancelled_at;

  return (
    <div className="group-detail-page">
      {toastElement}
      <div className="page-header">
        <div>
          <div className="group-ref-row">
            <h1>{group.group_ref} — {group.title}</h1>
            {group.is_order && <span className="order-badge">Order</span>}
            <span className={`chip ${group.status === 'cancelled' ? 'chip-red' : group.status === 'complete' ? (OUTCOME_META[group.outcome]?.chip || 'chip-green') : 'chip-teal'}`}>
              {group.status === 'complete' && group.outcome ? OUTCOME_META[group.outcome].label : group.status}
            </span>
          </div>
          <p>
            <Link to={`/customers/${group.customer_id}`}>{group.customer_name}</Link>
            {' · '}{group.route === 'distributor' ? `Distributor — ${group.distributor_name}` : 'Direct'}
            {group.farm_site_name ? ` · ${group.farm_site_name}` : ''}
            {group.owner_name ? ` · Owner: ${group.owner_name}` : ''}
          </p>
        </div>
        <div className="page-header-actions">
          <span className="group-value">Value: {formatMoney(group.computed_value_minor_units, currency)}</span>
          {canManage && group.status === 'active' && (
            <button className="btn-danger" disabled={busy} onClick={() => setCancelTarget({ type: 'group' })}>
              {group.is_order ? 'Cancel Order' : 'Cancel Opportunity'}
            </button>
          )}
          <button className="btn-secondary" onClick={() => backToList()}>Back</button>
        </div>
      </div>

      {actionError && <div className="form-error">{actionError}</div>}

      {group.is_order && (
        <OrderAcceptance
          group={group}
          canAccept={canAcceptOrders(user?.role)}
          authFetch={authFetch}
          onChanged={async () => { await load(); setHistoryKey(k => k + 1); }}
          showToast={showToast}
        />
      )}

      {group.status === 'complete' && (
        <div className="group-complete-banner">
          This opportunity is complete — every line item has finished its journey. It no longer
          appears in the active list; its full history stays here and on the customer timeline.
        </div>
      )}

      {pendingRequests.length > 0 && (
        <div className="approval-panel">
          <div className="approval-panel-head">
            <h2>Awaiting your approval ({pendingRequests.length})</h2>
            {pendingRequests.length > 1 && (
              <button className="btn-primary btn-small" disabled={busy} onClick={approveAll}>
                Approve All
              </button>
            )}
          </div>
          {pendingRequests.map(request => (
            <div key={request.request_id} className="approval-row">
              <div className="approval-row-info">
                <strong>{request.opp_ref}</strong>
                {' — '}{request.product ? request.product.replace(/_/g, ' ') : request.line_description}
                {' · '}{formatMoney(request.value_minor_units, request.currency)}
                <div className="approval-row-reason">
                  Requested by {request.requested_by_name}{request.reason ? ` — "${request.reason}"` : ''}
                </div>
              </div>
              <div className="approval-row-actions">
                <input
                  placeholder="Note (required to reject)…"
                  value={notes[request.request_id] || ''}
                  onChange={(e) => setNotes(prev => ({ ...prev, [request.request_id]: e.target.value }))}
                />
                <button className="btn-primary btn-small" disabled={busy} onClick={() => decideRequest(request, 'approved')}>
                  Approve
                </button>
                <button className="btn-danger btn-small" disabled={busy} onClick={() => decideRequest(request, 'rejected')}>
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="detail-section">
        <h2>Line Items ({items.length})</h2>
        <table className="group-items-table">
          <thead>
            <tr>
              <th>Ref</th>
              <th>Line</th>
              <th>Stage</th>
              <th>Value</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => {
              const status = lineStatus(item);
              return (
                <tr key={item.opportunity_id} className={item.cancelled_at ? 'line-cancelled' : ''}>
                  <td>
                    {item.line_type === 'product'
                      ? <Link to={`/pipeline/${item.opportunity_id}`}>{item.opp_ref}</Link>
                      : item.opp_ref}
                  </td>
                  <td>
                    {item.line_type === 'product' ? (item.product || '').replace(/_/g, ' ') : `${item.description} (non-product)`}
                    {/* What a parts order is (Gareth 2026-10-19). */}
                    {item.line_type === 'product' && item.description && <div className="line-what">{item.description}</div>}
                  </td>
                  <td>{item.line_type === 'product' ? item.stage : '—'}</td>
                  <td>{formatMoney(item.value_minor_units, item.currency)}</td>
                  <td>
                    {status ? <span className={`chip ${status.className}`}>{status.label}</span> : <span className="chip chip-teal">Active</span>}
                    {Number(item.po_count) > 0 && <span className="chip chip-muted" title={`${item.po_count} purchase order(s) linked`}> PO</span>}
                    {item.asset_id && (
                      <Link
                        to={`/assets/${item.asset_id}`}
                        className={`chip ${item.asset_status === 'pending_install' ? 'chip-amber' : 'chip-teal'}`}
                        title={item.asset_status === 'pending_install'
                          ? 'Asset generated at won — complete it (serial, site, install date) before the install-complete stage'
                          : 'The asset record for this line item'}
                      >
                        {item.asset_ref}{item.asset_status === 'pending_install' ? ' · pending' : ''}
                      </Link>
                    )}
                  </td>
                  <td className="line-actions">
                    {canManage && ownItem(item) && (
                      <button className="btn-link" disabled={busy} onClick={() => setReviseItem(item)}>
                        {item.approval_status === 'rejected' ? 'Revise pricing' : 'Edit pricing'}
                      </button>
                    )}
                    {lockedItem(item) && (
                      <span className="chip chip-muted" title="Won — pricing can only change via an approved contract variation">
                        Locked
                      </span>
                    )}
                    {lockedItem(item) && item.has_pending_variation && (
                      <span className="chip chip-amber" title="A contract variation is awaiting approval">
                        Variation pending
                      </span>
                    )}
                    {canManage && lockedItem(item) && !item.has_pending_variation && (
                      <button className="btn-link" disabled={busy} onClick={() => setVariationItem(item)}>
                        Request variation
                      </button>
                    )}
                    {canManage && ownItem(item) && item.line_type !== 'product' && (
                      <button className="btn-link" disabled={busy} onClick={() => setWonTarget(item)}>
                        Mark Won
                      </button>
                    )}
                    {canManage && ownItem(item) && !item.won_at && (
                      <button className="btn-link btn-link-danger" disabled={busy} onClick={() => setLostItem(item)}>
                        Mark Lost
                      </button>
                    )}
                    {canManage && ownItem(item) && (
                      <button className="btn-link btn-link-danger" disabled={busy} onClick={() => setCancelTarget({ type: 'line', item })}>
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {group.notes && (
          <div className="group-notes">
            <h3>Notes</h3>
            <p>{group.notes}</p>
          </div>
        )}
      </div>

      <div className="detail-section">
        <div className="section-head-row">
          <h2>Contracts &amp; Documents</h2>
          {canManage && group.status === 'active' && (
            <button
              className="btn-primary btn-small"
              onClick={() => navigate(`/quotes/new?group_id=${id}`)}
            >
              Generate Contract
            </button>
          )}
          {/* Operations coordinators produce order acknowledgements (Gareth 2026-10-19). */}
          {!canManage && user?.role === 'operations_coordinator' && group.status === 'active'
            && group.is_order && group.order_accepted_at && (
            <button
              className="btn-primary btn-small"
              onClick={() => navigate(`/quotes/new?group_id=${id}`)}
            >
              Generate Order acknowledgement
            </button>
          )}
        </div>
        {items.some(item => item.line_type === 'product' && !item.lost_at && !item.cancelled_at
          && ['New Lead', 'Low Interest', 'High Interest', 'Customer visit required', 'Awaiting site survey'].includes(item.stage)) && (
          <p className="survey-hint">
            Tip: contracts are normally generated after the site survey is complete — one or more lines
            haven't reached that point yet.
          </p>
        )}
        {quotes.length === 0 ? (
          <p className="field-hint">No documents generated yet.</p>
        ) : (
          <table className="group-items-table">
            <thead>
              <tr><th>Ref</th><th>Type</th><th>Status</th><th>Value</th><th>Created</th></tr>
            </thead>
            <tbody>
              {quotes.map(quote => (
                <tr key={quote.quote_id}>
                  <td><Link to={`/quotes/${quote.quote_id}`}>{quote.quote_ref}</Link></td>
                  <td>{quote.doc_type === 'quote' ? 'Quote' : 'Contract'}{quote.is_contract ? ' ✔' : ''}</td>
                  <td><span className="chip chip-teal">{quote.status}</span></td>
                  <td>{formatMoney(Number(quote.subtotal_minor_units || 0), quote.currency)}</td>
                  <td>{new Date(quote.created_at).toLocaleDateString('en-GB')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="detail-section">
        <div className="section-head-row">
          <h2>Purchase Orders</h2>
          {canManage && group.status === 'active' && (() => {
            const eligible = items.some(item => item.line_type === 'product' && !item.lost_at && !item.cancelled_at
              && (stagesByType[item.product] || []).some(stage => stage.stage_name === item.stage && stage.po_upload_enabled));
            return (
              <button
                className="btn-primary btn-small"
                disabled={!eligible || busy}
                title={eligible ? 'Upload a purchase order' : 'Available once a line item reaches a PO-enabled stage (e.g. Contract Signed)'}
                onClick={() => setShowPoModal(true)}
              >
                Upload PO
              </button>
            );
          })()}
        </div>
        {purchaseOrders.length === 0 ? (
          <p className="field-hint">No purchase orders yet — POs are optional (a signed contract is the order) and can cover one, several, or all line items.</p>
        ) : (
          <table className="group-items-table">
            <thead>
              <tr><th>PO number</th><th>File</th><th>Covers</th><th>Notes</th><th>Uploaded</th></tr>
            </thead>
            <tbody>
              {purchaseOrders.map(po => (
                <tr key={po.po_id}>
                  <td>{po.po_number || '—'}</td>
                  <td>
                    {po.attachment?.url ? (
                      <a href={authedFileHref(po.attachment.url)} target="_blank" rel="noreferrer">📎 {po.attachment.name}</a>
                    ) : '—'}
                  </td>
                  <td>
                    {(po.line_items || []).length > 0
                      ? po.line_items.map(line => line.opp_ref).join(', ')
                      : 'Whole opportunity'}
                  </td>
                  <td>{po.notes || '—'}</td>
                  <td>{po.uploaded_by_name || '—'} · {new Date(po.created_at).toLocaleDateString('en-GB')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="detail-section">
        <h2>History</h2>
        <AuditLogPanel
          key={historyKey}
          fetchUrl={`/api/v1/opportunity-groups/${id}/history`}
          emptyMessage="No history yet."
        />
      </div>

      {reviseItem && (
        <RevisePricingModal
          item={reviseItem}
          authFetch={authFetch}
          onClose={() => setReviseItem(null)}
          onSaved={() => { setReviseItem(null); load(); }}
        />
      )}
      {variationItem && (
        <VariationModal
          line={variationItem}
          onClose={() => setVariationItem(null)}
          onSubmitted={() => {
            setVariationItem(null);
            showToast('Variation submitted for approval', 'success');
            setHistoryKey(k => k + 1);
            load();
          }}
        />
      )}
      {showPoModal && (
        <UploadPoModal
          group={group}
          eligibleIds={new Set(items
            .filter(item => item.line_type === 'product' && !item.lost_at && !item.cancelled_at
              && (stagesByType[item.product] || []).some(stage => stage.stage_name === item.stage && stage.po_upload_enabled))
            .map(item => item.opportunity_id))}
          authFetch={authFetch}
          onClose={() => setShowPoModal(false)}
          onDone={() => { setShowPoModal(false); setHistoryKey(k => k + 1); load(); }}
        />
      )}
      {wonTarget && (
        <ConfirmDialog
          title="Mark line item won?"
          message={`${wonTarget.description || wonTarget.opp_ref} will be marked won and its price locked — later changes need an approved contract variation.`}
          confirmLabel="Mark Won"
          onConfirm={() => markWon(wonTarget)}
          onCancel={() => setWonTarget(null)}
        />
      )}
      {lostItem && (
        <MarkLostModal
          item={lostItem}
          authFetch={authFetch}
          onClose={() => setLostItem(null)}
          onDone={() => { setLostItem(null); setHistoryKey(k => k + 1); load(); }}
        />
      )}
      {cancelTarget && (
        <ReasonModal
          title={cancelTarget.type === 'group' ? `Cancel ${group.group_ref}?` : `Cancel ${cancelTarget.item.opp_ref}?`}
          actionLabel={cancelTarget.type === 'group' ? 'Cancel Opportunity' : 'Cancel Line Item'}
          onClose={() => setCancelTarget(null)}
          onConfirm={(reason) => (cancelTarget.type === 'group' ? cancelGroup(reason) : cancelLine(cancelTarget.item, reason))}
        />
      )}
    </div>
  );
}

export default OpportunityGroupDetail;
